import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Filter, Search, ChevronDown, Calendar, Clock,
  DollarSign, FileText, AlertCircle, CheckCircle,
  XCircle, Loader, MessageCircle, Star, Download,
  ArrowUpRight, ArrowDownLeft, ExternalLink, Menu,
  User, MapPin, Timer, X, File, Eye, Paperclip, ChevronRight
} from "lucide-react";
import { Card } from "../components/ui/card";
import SharedHeader4 from "../Headers/SharedHeader4";
import SharedFooter3 from "../Footer/SharedFooter3";
import { useSelector } from "react-redux";
import { createChat, createConversation, fetchParticipantChats, getProfessionalStats, updateChat, useAppDispatch } from "../redux";
import { fetchContracts, updateContract } from "../redux/Slices/contractSlice";
import { updateMilestone } from "../redux/Slices/milestoneSlice";
import { calculateUserAverageRating } from "../utils/ratingCalculationUtils";
import { Badge } from "../components/ui/badge";
import ClientProfile1 from "../modules/client/components/ClientProfile1";
import { Escrow, escrowAPI } from "../services/escrowAPI";
import { ContextEnum, ContractStatusEnum, ConversationTypeEnum, MilestoneStatusEnum, RefundRequestStatusEnum } from "../modules/shared/model";
import { GlobalService } from "../modules";
import SmartWorkTrackerServicesDialog from "../components/ui/SmartWorkTrackerServicesDialog";
import OrderDetailsModal from "../components/ui/OrderDetailsModal";
import {
  getContractStatusText,
  getContractStatusColor,
  getMilestoneStatusText,
  getMilestoneStatusColor,
  getPaymentStatusText,
  getPaymentStatusColor,
  canAcceptDeclineContract,
  requiresTrackerCheckbox,
  defaultHandleContractAction,
  defaultHandleRequestPaymentRelease,
  defaultHandleTrackerCheckboxChange
} from "../utils/contractUtils";
import { checkUserBillingInfo } from "../utils/billingUtils";
import BillingAddressRequirementDialog from "../components/ui/BillingAddressRequirementDialog";
import ProfileCompletionDialog from "../components/ui/ProfileCompletionDialog";
import VerificationRequirementDialog from "../components/VerificationRequirementDialog";
import { useVerification } from "../hooks/useVerification";
import { RoleEnum } from "../modules";
import { useNavigate } from "react-router-dom";
import { getDynamicCurrencySymbol } from "../lib/currencyUtils";
import { checkCanCommunicate, trackActivity } from "../redux/Actions/ProfessionalMonitoringActions";
import { useTranslation } from "react-i18next";

const OrdersDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // State management
  const [activeTab, setActiveTab] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [dateRange, setDateRange] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Client profile dialog state
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [selectedClientUser, setSelectedClientUser] = useState<any>(null);

  // Milestones state
  const [milestonesData, setMilestonesData] = useState<{ [contractId: string]: any[] }>({});

  // Tracker checkbox state
  const [trackerCheckboxStates, setTrackerCheckboxStates] = useState<{ [contractId: string]: boolean }>({});

  // Smart Work Tracker Services Dialog state
  const [showServicesDialog, setShowServicesDialog] = useState(false);

  // Loading states for contract actions
  const [contractActionLoading, setContractActionLoading] = useState<{ [contractId: string]: string | null }>({});

  // Billing and verification dialog states
  const [showBillingAddressDialog, setShowBillingAddressDialog] = useState(false);
  const [selectedContractForBilling, setSelectedContractForBilling] = useState<any>(null);
  const [showProfileCompletionDialog, setShowProfileCompletionDialog] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  
  // Use verification hook
  const { isVerified, goToVerification, verificationData } = useVerification();
  
  // Track if verification data has been loaded at least once
  const [verificationDataLoaded, setVerificationDataLoaded] = useState(false);

  // Contracts data (professional perspective)
  const dispatch = useAppDispatch();
  const { user } = useSelector((state: any) => state.auth);
  const { professionalStats } = useSelector((state: any) => state.professional);
  const [contracts, setContracts] = useState<any[]>([]);
  const [allContracts, setAllContracts] = useState<any[]>([]); // Store all contracts for filtering
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 10, totalCount: 0, totalPages: 0, hasNextPage: false });


  const loadContracts = async (page = 1, limit = 10) => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const query: any = { page, limit, professionalId: user.id, sort: JSON.stringify([{ orderBy: "createdAt", order: "DESC" }]) };
      // Don't filter by status on backend - we'll filter on frontend
      if (searchQuery) query.search = searchQuery;
      const res: any = await dispatch(fetchContracts(query));
      if (res?.meta?.requestStatus === "fulfilled") {
        const payload = res.payload || {};
        const data = payload.data || [];
        setAllContracts(data); // Store all contracts
        const filteredContracts = filterContractsByTab(data, activeTab);
        setContracts(filteredContracts);
        const totalCount = payload.totalCount || 0;
        const totalPages = Math.ceil(totalCount / limit) || 0;
        setPagination({ currentPage: page, limit, totalCount, totalPages, hasNextPage: page < totalPages });

        // Fetch milestones for each contract
        await loadMilestonesForContracts(data);
      }
    } finally { setLoading(false); }
  };

  // Filter contracts based on active tab
  const filterContractsByTab = (contracts: any[], tab: string) => {
    if (tab === "all") return contracts;
    
    return contracts.filter(contract => {
      const status = Number(contract?.status?.id);
      switch (tab) {
        case "active":
          // Show Active and Accepted status orders
          return status === ContractStatusEnum.Active || status === ContractStatusEnum.Accepted;
        case "completed":
          // Show Completed and Closed status orders
          return status === ContractStatusEnum.Completed || status === ContractStatusEnum.Closed;
        case "cancelled":
          // Show Rejected, Cancelled, and Deleted status orders
          return status === ContractStatusEnum.Rejected || 
                 status === ContractStatusEnum.Cancelled || 
                 status === ContractStatusEnum.Deleted;
        default:
          return true;
      }
    });
  };

  // Update contracts when tab changes
  useEffect(() => {
    if (allContracts.length > 0) {
      const filteredContracts = filterContractsByTab(allContracts, activeTab);
      setContracts(filteredContracts);
    }
  }, [activeTab, allContracts]);

  // Calculate stats based on all contracts and professional stats
  const stats = useMemo(() => {
    const all = allContracts.length;
    const completed = allContracts.filter(c => {
      const status = Number(c?.status?.id);
      return status === ContractStatusEnum.Completed || status === ContractStatusEnum.Closed;
    }).length;
    const active = allContracts.filter(c => {
      const status = Number(c?.status?.id);
      return status === ContractStatusEnum.Active || status === ContractStatusEnum.Accepted;
    }).length;
    const cancelled = allContracts.filter(c => {
      const status = Number(c?.status?.id);
      return status === ContractStatusEnum.Rejected ||
             status === ContractStatusEnum.Cancelled ||
             status === ContractStatusEnum.Deleted;
    }).length;

    const totalRevenue = Number(professionalStats?.balance ?? 0);

    return { all, completed, active, cancelled, totalRevenue };
  }, [allContracts, professionalStats]);

  const loadMilestonesForContracts = async (contracts: any[]) => {
    const milestonesMap: { [contractId: string]: any[] } = {};

    for (const contract of contracts) {
      try {
        // Get milestones from contract data instead of making separate API call
        const milestones: any = contract.milestones || [];

        if (milestones.length > 0) {
          // Get escrows from contract data instead of making separate API call
          const escrows: any = contract.escrows || [];

          // Map escrows to milestones
          const milestonesWithEscrows = milestones.map((milestone: any) => {
            const escrow = escrows.find((e: any) => e.milestone?.id === milestone.id);
            let paymentStatus = "no_payment"; // Default status

            if (escrow) {
              const statusName = escrow.status?.name?.toLowerCase() || escrow.status?.toString().toLowerCase() || "";
              switch (statusName) {
                case "paid":
                case "funded":
                case "completed":
                  paymentStatus = "paid";
                  break;
                case "pending":
                case "created":
                  paymentStatus = "pending";
                  break;
                case "failed":
                case "cancelled":
                  paymentStatus = "failed";
                  break;
                case "released":
                  paymentStatus = "released";
                  break;
                case "refunded":
                  paymentStatus = "refunded";
                  break;
                default:
                  paymentStatus = "pending";
              }
            }
            return {
              ...milestone,
              escrow,
              paymentStatus
            };
          });
          milestonesMap[contract.id] = milestonesWithEscrows;
        } else {
          milestonesMap[contract.id] = [];
        }
      } catch (error) {
        console.error(`Failed to load milestones for contract ${contract.id}:`, error);
        milestonesMap[contract.id] = [];
      }
    }

    setMilestonesData(milestonesMap);
  };

  useEffect(() => {
    loadContracts(1, pagination.limit);
  }, [user?.id]);
  useEffect(() => {
    if (user?.id) {
      dispatch(getProfessionalStats({ id: user.id.toString(), isShowLoader: false }));
    }
  }, [dispatch, user?.id]);
  useEffect(() => {
    // refetch when search changes (but not statusFilter since we filter on frontend)
    const t = setTimeout(() => loadContracts(1, pagination.limit), 400);
    return () => clearTimeout(t);
  }, [searchQuery, sortBy, dateRange]);

  const handlePageChange = (p: number) => {
    if (p >= 1 && p <= pagination.totalPages) loadContracts(p, pagination.limit);
  };





  // Handle tracker checkbox change
  const handleTrackerCheckboxChange = (contractId: string, checked: boolean) => {
    defaultHandleTrackerCheckboxChange(contractId, checked, setTrackerCheckboxStates);
  };

  // Check if user needs to complete profile
  const checkProfileCompletion = () => {
    if (user && !user.phone) {
      setShowProfileCompletionDialog(true);
      return false; // Profile incomplete
    }
    return true; // Profile complete
  };

  // Track when verification data has loaded
  useEffect(() => {
    if (verificationData !== null && verificationData !== undefined) {
      setVerificationDataLoaded(true);
    }
  }, [verificationData]);

  // Check if user needs verification
  const checkVerification = () => {
    // Don't check verification if data hasn't loaded yet
    if (!verificationDataLoaded) {
      return true; // Allow action to proceed while loading
    }
    
    if (!isVerified()) {
      setShowVerificationDialog(true);
      return false; // Verification incomplete
    }
    return true; // Verification complete
  };

  // Check both profile and verification
  const checkRequirements = () => {
    // Don't block actions if verification data is still loading
    if (!verificationDataLoaded) {
      // Only check profile completion while verification loads
      if (!checkProfileCompletion()) {
        return false;
      }
      return true;
    }
    
    // Check verification first (higher priority)
    if (!checkVerification()) {
      return false;
    }
    
    // Then check profile completion
    if (!checkProfileCompletion()) {
      return false;
    }
    
    return true;
  };

  // Helper function to navigate to profile based on user role
  const navigateToProfile = () => {
    if (!user?.role?.id) return;
    
    const roleId = Number(user.role.id);
    if (roleId === RoleEnum.professional || roleId === RoleEnum.internationl_professional) {
      navigate("/myprofile");
    } else if (roleId === RoleEnum.client || roleId === RoleEnum.internationl_client) {
      navigate("/client/profile");
    } else if (roleId === RoleEnum.admin) {
      navigate("/admin/dashboard");
    }
    setShowProfileCompletionDialog(false);
  };

  // Handle billing saved callback
  const handleBillingSaved = () => {
    // Check if user now has billing information
    const billingCheck = checkUserBillingInfo(user);
    
    if (billingCheck.hasBillingInfo && selectedContractForBilling) {
      // Proceed with accepting the contract
      handleContractAction(selectedContractForBilling, "accept");
      setSelectedContractForBilling(null);
    }
  };

  // Helper function to get refund request status info
  const getRefundRequestInfo = (refundRequest: any) => {
    if (!refundRequest) return null;
    
    const statusId = Number(refundRequest.status?.id);
    
    switch (statusId) {
      case RefundRequestStatusEnum.Active:
        return {
          text: t("myOrdersPage.refundRequested"),
          className: "bg-yellow-100 text-yellow-800",
          showReason: false
        };
      case RefundRequestStatusEnum.Approved:
        return {
          text: t("myOrdersPage.refundRequestApproved"),
          className: "bg-green-100 text-green-800",
          showReason: false
        };
      case RefundRequestStatusEnum.Rejected:
        return {
          text: t("myOrdersPage.refundRequestRejected"),
          className: "bg-red-100 text-red-800",
          showReason: true,
          reason: refundRequest.rejectReason || t("myOrdersPage.noReasonProvided")
        };
      default:
        return {
          text: t("myOrdersPage.refundRequest"),
          className: "bg-gray-100 text-gray-800",
          showReason: false
        };
    }
  };

  // Handle contract acceptance/decline
  const handleContractAction = async (contractId: string, action: "accept" | "decline") => {
    // Check profile and verification requirements first
    if (!checkRequirements()) {
      return; // Stop execution if requirements not met
    }

    // Check communication restrictions if user is a professional
    const userRoleId = Number(user?.role?.id);
    const isProfessional = userRoleId === RoleEnum.professional || userRoleId === RoleEnum.internationl_professional;
    
    if (isProfessional) {
      const contract = contracts.find(c => c.id === contractId);
      console.log("🔍 Contract:", contract);
      // Get client ID from contract structure - try multiple paths
      const clientId = contract?.client?.id;
      
      if (clientId) {
        try {
          const commCheck: any = await dispatch(checkCanCommunicate({ 
            professionalId: user.id, 
            clientId: clientId 
          })).unwrap();
          
          if (!commCheck.canCommunicate) {
            const suspensionEndDate = commCheck.suspensionEndDate 
              ? new Date(commCheck.suspensionEndDate).toLocaleDateString() 
              : "unknown date";
            
            if (commCheck.suspensionType === "new_clients_only") {
              GlobalService.showToast(
                "error", 
                t("myOrdersPage.contractActionRestricted"), 
                t("myOrdersPage.suspendedNewClientsMessage", { date: suspensionEndDate })
              );
            } else {
              GlobalService.showToast(
                "error", 
                t("myOrdersPage.contractActionRestricted"), 
                t("myOrdersPage.suspendedAllClientsMessage", { date: suspensionEndDate })
              );
            }
            return;
          }
        } catch (error) {
          // Silent error - allow action if check fails
        }
      }
    }
    // Check if user has billing information for accepting contracts
    const billingCheck = checkUserBillingInfo(user);
    
    if (!billingCheck.hasBillingInfo && action === "accept") {
      // Store the contract ID for auto-accepting after billing setup
      setSelectedContractForBilling(contractId);
      // Show billing address requirement dialog
      setShowBillingAddressDialog(true);
      return;
    }

    // Set loading state for this specific contract and action
    setContractActionLoading(prev => ({ ...prev, [contractId]: action }));
    
    try {
      // Find the contract
      const contract = contracts.find(c => c.id === contractId);
      if (!contract) return;

      // Find the first milestone
      const contractMilestones = milestonesData[contractId] || [];
      const firstMilestone = contractMilestones[0];

      if (!firstMilestone) {
        GlobalService.showToast("error", "Error", t("myOrdersPage.errorNoMilestone"));
        return;
      }

      // Check if escrow is funded
      const isEscrowFunded = firstMilestone.paymentStatus === "paid";

      if (!isEscrowFunded) {
        GlobalService.showToast("error", "Error", t("myOrdersPage.errorEscrowMustBeFunded"));
        return;
      }

      // Show confirmation alert for decline action
      if (action === "decline") {
        const confirmed = window.confirm(t("myOrdersPage.declineContractConfirmation"));

        if (!confirmed) {
          return; // User cancelled the action
        }
      }

      // Update milestone status
      const milestoneStatus = action === "accept" ? MilestoneStatusEnum.Accepted : MilestoneStatusEnum.Rejected;
      await dispatch(updateMilestone({
        id: firstMilestone.id,
        data: {
          status: { id: milestoneStatus }
        }
      }));

      const contractStatus = action === "accept" ? ContractStatusEnum.Accepted : ContractStatusEnum.Rejected;
      await dispatch(updateContract({
        id: contractId,
        data: {
          status: { id: contractStatus }
        }
      }));

      // Track professional activity when contract is accepted
      if (action === "accept") {
        try {
          const contract = contracts.find(c => c.id === contractId);
          const clientId = contract?.client?.id;
          
          if (user?.id && clientId) {
            await dispatch(trackActivity({
              professionalId: user.id.toString(),
              activityType: "contract_created",
              contractId: contractId,
              clientId: clientId.toString(),
              amount: contract?.amount || 0,
              metadata: {
                professionalType: "home_service",
                category: "general"
              }
            })).unwrap();
          }
        } catch (trackError) {
          console.error("Failed to track contract creation:", trackError);
          // Silent error handling - don't fail the contract acceptance if tracking fails
        }
      }

      // If declining, refund the escrow
      if (action === "decline") {
        try {
          await escrowAPI.refundByMilestone(firstMilestone.id);
          GlobalService.showToast("success", t("toast.titles.success"), t("myOrdersPage.escrowRefundedSuccess"));
        } catch (error: any) {
          console.error("Failed to refund escrow:", error);
          GlobalService.showToast("error", "Error", t("myOrdersPage.escrowRefundFailed", { error: error?.message || "Unknown error" }));
        }
      }

      // Refresh contracts and milestones
      await loadContracts(pagination.currentPage, pagination.limit);

      GlobalService.showToast(
        "success",
        "Success",
        action === "accept" ? t("myOrdersPage.contractAcceptedSuccess") : t("myOrdersPage.contractDeclinedSuccess")
      );
    } catch (error: any) {
      GlobalService.showToast("error", "Error", error?.message || t("myOrdersPage.contractUpdateFailed"));
    } finally {
      // Clear loading state for this contract
      setContractActionLoading(prev => ({ ...prev, [contractId]: null }));
    }
  };

  // Handle payment release request
  const handleRequestPaymentRelease = async (milestoneId: string) => {
    try {
      // Find the milestone and its escrow
      const contractId = Object.keys(milestonesData).find(id =>
        milestonesData[id].some(m => m.id === milestoneId)
      );

      if (!contractId) {
        GlobalService.showToast("error", "Error", "Contract not found for this milestone");
        return;
      }

      const milestone = milestonesData[contractId].find(m => m.id === milestoneId);
      if (!milestone || !milestone.escrow) {
        GlobalService.showToast("error", "Error", "Escrow not found for this milestone");
        return;
      }

      await createChatAndSendMessage(milestone.escrow);

      // Call the release payment API
      // await escrowAPI.releasePayment(milestone.escrow.id, user?.id?.toString() || "");

      // Refresh contracts and milestones
      await loadContracts(pagination.currentPage, pagination.limit);

      GlobalService.showToast("success", t("toast.titles.success"), t("toast.payments.releaseRequested"));
    } catch (error: any) {
      console.error("Failed to request payment release:", error);
      GlobalService.showToast("error", "Error", "Failed to request payment release: " + (error?.message || "Unknown error"));
    }
  };

  // Function to create chat and send payment success message
  const createChatAndSendMessage = async (escrow: Escrow) => {
    try {
      // Check if chat already exists between client and professional
      let existingChat = null;
      try {
        existingChat = await dispatch(fetchParticipantChats({ currentUserId: escrow?.professional?.id })).unwrap();
      } catch (error) {
        console.log("No existing chat found, will create new one");
      }
      let chatResponse;
      if (existingChat && existingChat.length > 0) {
        const filteredChats = existingChat.filter(chat => {
          const participantIds = chat.chat.participants.map(p => p.id);
          return participantIds.includes(escrow.client.id) && participantIds.includes(escrow.professional.id);
        });
        const chat = filteredChats[0].chat;
        chatResponse = chat;
        // Update the chat with lastMessage and lastMessageAt
        if (chatResponse && chatResponse.id) {
          const updatedChat = {
            lastMessage: t("myOrdersPage.chat.paymentReleaseRequest"),
            lastMessageAt: new Date().toISOString(),
            unreadCount: { [escrow.client?.id]: 1, [escrow.professional?.id]: 0 },
          };
          await dispatch(updateChat({
            chatId: chatResponse.id,
            data: updatedChat

          }));
        }
      } else {
        const chatPayload = {
          unreadCount: { [escrow.client?.id]: 0, [escrow.professional?.id]: 1 },
          participants: [
            { id: escrow.client?.id },
            { id: escrow.professional?.id }
          ],
          contextId: escrow?.contract?.id,
          contextType: { id: ContextEnum.Message },
          lastMessageAt: new Date().toISOString(),
          lastMessage: t("myOrdersPage.chat.paymentReleaseRequest"),
        };
        chatResponse = await dispatch(createChat({ data: chatPayload })).unwrap();
      }

      // Send payment success message
      const conversationPayload = {
        isRead: false,
        message: t("myOrdersPage.chat.paymentReleaseRequested"),
        sender: { id: escrow.client?.id },
        chatId: { id: chatResponse.id },
        contextId: { id: escrow?.id },
        type: { id: ConversationTypeEnum.Payment_Request },
        attachments: [],
      };

      await dispatch(createConversation({ data: conversationPayload }));
      console.log("Payment success message sent");

    } catch (error) {
      console.error("Error creating chat or sending message:", error);
      // Don't throw error here as payment was successful
    }
  };

  // Map contract record to UI shape
  const mapContractToOrder = (c: any) => {
    const contractMilestones = milestonesData[c.id] || [];
    const contractType = Number(c?.type?.id) === 2 ? "hourly" : "fixed";
    const isHourly = contractType === "hourly";

    // Calculate platform fees and totals
    const calculatePlatformFee = (amount: number) => amount * 0.02; // 2% platform fee
    const calculateTotalWithFee = (amount: number) => amount;

    // Check if contract is emergency and get emergency cost from first milestone
    const isEmergency = c?.offer?.job?.isEmergency || c?.isEmergency || false;
    const firstMilestone = contractMilestones[0];
    const emergencyCost = isEmergency && firstMilestone ? Number(firstMilestone.emergencyCost || 0) : 0;
    const trackerCost = firstMilestone ? Number(firstMilestone.trackerAmount || 0) : 0;

    // For hourly contracts, calculate weekly amount
    const weeklyAmount = isHourly ? (Number(c.amount || 0) * Number(c.weeklyLimit || 0)) : Number(c.amount || 0);
    const platformFee = calculatePlatformFee(weeklyAmount + emergencyCost + trackerCost);
    const totalAmount = calculateTotalWithFee(weeklyAmount + emergencyCost);

    // Calculate average rating for the client from milestone reviews
    const clientId = c?.client?.id;
    let clientRatingData = { average: 0, count: 0 };

    if (clientId) {
      // Extract all reviews from all milestones in this contract
      const allContractReviews = contractMilestones.flatMap(milestone => milestone.reviews || []);
      clientRatingData = calculateUserAverageRating(allContractReviews, user.id);
    }

    return {
      id: c.id,
      title: c.title || "Contract",
      client: c?.client?.firstName ? `${c.client.firstName} ${c.client.lastName || ""}`.trim() : (c?.client?.id || "Client"),
      clientInfo: {
        ...c?.client,
        rating: c.client.averageRating,
        reviews: c.client.reviewsCount
      },
      amount: Number(c.amount || 0),
      weeklyAmount: weeklyAmount,
      platformFee: platformFee,
      totalAmount: totalAmount,
      contractType: contractType,
      isHourly: isHourly,
      weeklyLimit: c.weeklyLimit || 0,
      enableTracker: c.enableTracker || false,
      status: Number(c?.status?.id),
      date: c.createdAt || new Date().toISOString(),
      dueDate: c.dueDate || c.updatedAt || new Date().toISOString(),
      description: c.description || "",
      location: c.location || "",
      messages: 0,
      attachments: c?.files?.length || 0,
      files: c?.files || [],
      clientRating: clientRatingData.average,
      isPriority: false,
      paymentStatus: "paid",
      timeline: [],
      milestones: contractMilestones,
      // Emergency and tracker information
      isEmergency: isEmergency,
      emergencyCost: emergencyCost,
      trackerCost: trackerCost,
      // Refund request information
      refundRequest: c?.refundRequest || null
    };
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getContractStatusColor(status)}`}>
        {getContractStatusText(status)}
      </span>
    );
  };

  // Contract type badge component
  const ContractTypeBadge = ({ contractType, isHourly, weeklyLimit }) => {
    if (isHourly) {
      return (
        <div className="flex items-center gap-2">
          <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
            {t("myOrdersPage.hourly")}
          </span>
          <span className="text-xs text-gray-500">
            {t("myOrdersPage.hoursPerWeek", { hours: weeklyLimit })}
          </span>
        </div>
      );
    }
    return (
      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
        {t("myOrdersPage.fixedPrice")}
      </span>
    );
  };

  // Star rating display component with decimal support
  const StarRatingDisplay = ({ rating, size = "sm" }) => {
    const sizeClasses = {
      sm: "h-4 w-4",
      md: "h-5 w-5",
      lg: "h-6 w-6"
    };

    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => {
          const isHalfFilled = rating >= star - 0.5 && rating < star;
          const isFilled = rating >= star;

          return (
            <div key={star} className="relative">
              <Star
                className={`${sizeClasses[size]} ${isFilled
                    ? "fill-yellow-400 text-yellow-400"
                    : "text-gray-300"
                  }`}
              />
              {isHalfFilled && (
                <div className="absolute left-0 top-0 w-1/2 h-full overflow-hidden">
                  <Star
                    className={`${sizeClasses[size]} fill-yellow-400 text-yellow-400`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };


  // Mobile menu tabs
  const MobileTabMenu = () => {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t lg:hidden z-40">
        <div className="grid grid-cols-4 gap-1 p-2">
          {[
            { id: "all", label: t("myOrdersPage.all"), icon: FileText, count: stats.all },
            { id: "active", label: t("myOrdersPage.active"), icon: Clock, count: stats.active },
            { id: "completed", label: t("myOrdersPage.done"), icon: CheckCircle, count: stats.completed },
            { id: "cancelled", label: t("myOrdersPage.cancelled"), icon: XCircle, count: stats.cancelled }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center justify-center p-2 rounded-lg relative ${activeTab === tab.id
                ? "text-blue-600 bg-blue-50"
                : "text-gray-600"
                }`}
            >
              <tab.icon className="h-5 w-5 mb-1" />
              <span className="text-xs">{tab.label}</span>
              {tab.count > 0 && (
                <span className={`absolute -top-1 -right-1 h-5 w-5 rounded-full text-xs flex items-center justify-center ${
                  activeTab === tab.id 
                    ? "bg-blue-600 text-white" 
                    : "bg-gray-400 text-white"
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  };


  return (
    <div className="min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
      <SharedHeader4 />

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{t("myOrdersPage.title")}</h1>
          <p className="text-gray-600 mt-1">{t("myOrdersPage.subtitle")}</p>
        </div>

        {/* Order Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <span className="text-sm text-gray-500">{t("myOrdersPage.allOrders")}</span>
            </div>
            <div className="mt-4">
              <h3 className="text-2xl font-bold text-gray-900">{stats.all}</h3>
              <p className="text-sm text-gray-600">{t("myOrdersPage.totalOrders")}</p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <span className="text-sm text-gray-500">{t("myOrdersPage.completed")}</span>
            </div>
            <div className="mt-4">
              <h3 className="text-2xl font-bold text-gray-900">{stats.completed}</h3>
              <p className="text-sm text-gray-600">{t("myOrdersPage.completedOrders")}</p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <span className="text-sm text-gray-500">{t("myOrdersPage.inProgress")}</span>
            </div>
            <div className="mt-4">
              <h3 className="text-2xl font-bold text-gray-900">{stats.active}</h3>
              <p className="text-sm text-gray-600">{t("myOrdersPage.activeOrders")}</p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-purple-600" />
              </div>
              <span className="text-sm text-gray-500">{t("myOrdersPage.revenue")}</span>
            </div>
            <div className="mt-4">
              <h3 className="text-2xl font-bold text-gray-900">{(stats.active > 0 || stats.completed > 0) ? getDynamicCurrencySymbol() : 0}{stats.totalRevenue.toFixed(2)}</h3>
              <p className="text-sm text-gray-600">{t("myOrdersPage.totalEarnings")}</p>
            </div>
          </Card>
        </div>

        {/* Filters and Search */}
        <Card className="mb-6">
          <div className="p-4 border-b">
            <div className="flex flex-col lg:flex-row justify-between gap-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full lg:w-auto">
                <div className="relative flex-1 lg:w-80">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t("myOrdersPage.searchOrders")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg"
                  />
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center justify-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50">
                  <Filter className="h-5 w-5" />
                  <span>{t("myOrdersPage.filters")}</span>
                </button>
              </div>

              <div className="flex items-center gap-4">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full sm:w-auto px-4 py-2 border rounded-lg"
                >
                  <option value="newest">{t("myOrdersPage.newestFirst")}</option>
                  <option value="oldest">{t("myOrdersPage.oldestFirst")}</option>
                  <option value="highest">{t("myOrdersPage.highestAmount")}</option>
                  <option value="lowest">{t("myOrdersPage.lowestAmount")}</option>
                </select>
              </div>
            </div>

            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="mt-4 pt-4 border-t overflow-hidden"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("myOrdersPage.dateRange")}
                      </label>
                      <select
                        value={dateRange}
                        onChange={(e) => setDateRange(e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="all">{t("myOrdersPage.allTime")}</option>
                        <option value="today">{t("myOrdersPage.today")}</option>
                        <option value="week">{t("myOrdersPage.thisWeek")}</option>
                        <option value="month">{t("myOrdersPage.thisMonth")}</option>
                        <option value="year">{t("myOrdersPage.thisYear")}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("myOrdersPage.status")}
                      </label>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="all">{t("myOrdersPage.allStatus")}</option>
                        <option value="completed">{t("myOrdersPage.completed")}</option>
                        <option value="in_progress">{t("myOrdersPage.inProgress")}</option>
                        <option value="pending">{t("myOrdersPage.pending")}</option>
                        <option value="cancelled">{t("myOrdersPage.cancelled")}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("myOrdersPage.paymentStatus")}
                      </label>
                      <select
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="all">{t("myOrdersPage.allPayments")}</option>
                        <option value="paid">{t("myOrdersPage.paid")}</option>
                        <option value="pending">{t("myOrdersPage.pending")}</option>
                        <option value="cancelled">{t("myOrdersPage.cancelled")}</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-end mt-4">
                    <button
                      onClick={() => {
                        setDateRange("all");
                        setStatusFilter("all");
                        setShowFilters(false);
                      }}
                      className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      {t("myOrdersPage.resetFilters")}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Order Tabs - Desktop */}
          <div className="hidden lg:block px-4 border-b">
            <div className="flex space-x-6">
              {[
                { id: "all", label: t("myOrdersPage.allOrders"), count: stats.all },
                { id: "active", label: t("myOrdersPage.active"), count: stats.active },
                { id: "completed", label: t("myOrdersPage.completed"), count: stats.completed },
                { id: "cancelled", label: t("myOrdersPage.cancelled"), count: stats.cancelled }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-2 relative flex items-center gap-2 ${activeTab === tab.id
                    ? "text-blue-600"
                    : "text-gray-600 hover:text-gray-900"
                    }`}
                >
                  {tab.label}
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    activeTab === tab.id 
                      ? "bg-blue-100 text-blue-600" 
                      : "bg-gray-100 text-gray-600"
                  }`}>
                    {tab.count}
                  </span>
                  {activeTab === tab.id && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
                    />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Orders List */}
          <div className="divide-y">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-4">
                  <Loader className="h-8 w-8 animate-spin text-blue-600" />
                  <p className="text-gray-600">{t("myOrdersPage.loadingContracts")}</p>
                </div>
              </div>
            ) : contracts.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-4">
                  <FileText className="h-12 w-12 text-gray-400" />
                  <div className="text-center">
                    <h3 className="text-lg font-medium text-gray-900">{t("myOrdersPage.noContractsFound")}</h3>
                    <p className="text-gray-600 mt-1">
                      {activeTab === "all" 
                        ? t("myOrdersPage.noContractsYet")
                        : t("myOrdersPage.noContractsInTab", { tab: activeTab })}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              contracts.map((c) => mapContractToOrder(c)).map((order) => (
                <div
                  key={order.id}
                  className="p-4 sm:p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() =>{
                    console.log("order", order);
                    setSelectedOrder(order);
                  } }
                >
                  <div className="flex flex-col lg:flex-row justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <h3 className="text-lg font-medium text-gray-900">
                          {order.title}
                        </h3>
                        <ContractTypeBadge
                          contractType={order.contractType}
                          isHourly={order.isHourly}
                          weeklyLimit={order.weeklyLimit}
                        />
                        {order.isPriority && (
                          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                            {t("myOrdersPage.priority")}
                          </span>
                        )}
                        {order.isEmergency && order.emergencyCost > 0 && (
                          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                            {t("myOrdersPage.emergency", { amount: `${getDynamicCurrencySymbol()}${order.emergencyCost.toFixed(2)}` })}
                          </span>
                        )}
                        {order.trackerCost > 0 && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                            {t("myOrdersPage.trackerEnabled")}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {t("myOrdersPage.orderId")}: {order.id} • {order.location}
                      </p>

                      {/* Client Information */}
                      {order.clientInfo && Object.keys(order.clientInfo).length > 0 && (
                        <div className="flex flex-col md:flex-row md:items-center gap-4 mt-2">
                          <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center shrink-0 overflow-hidden">
                            {(() => {
                              const c = order.clientInfo;
                              const photoUrl = c?.photo?.path || c?.photo?.url || c?.profilePhoto || "";
                              const name = (c?.firstName && c?.lastName) ? `${c.firstName} ${c.lastName}` : (c?.firstName || c?.name || "Client");
                              return photoUrl ? (
                                <img src={photoUrl} alt={name} className="h-12 w-12 rounded-full object-cover" />
                              ) : (
                                <User className="h-6 w-6 text-gray-600" />
                              );
                            })()}
                          </div>
                          <div className="space-y-1 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium">
                                {(order.clientInfo?.firstName && order.clientInfo?.lastName)
                                  ? `${order.clientInfo.firstName} ${order.clientInfo.lastName}`
                                  : (order.clientInfo?.firstName || order.clientInfo?.name || "Client")}
                              </span>
                              {order.clientInfo?.verified && (
                                <Badge variant={undefined} className="bg-green-50 text-green-700">{t("myOrdersPage.verified")}</Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                              <div className="flex items-center gap-1">
                                <Star className="h-4 w-4 text-yellow-400 fill-current" />
                                <span>{order.clientInfo?.rating || 0}</span>
                              </div>
                              {order.clientInfo?.reviews && (
                                <span>{t("myOrdersPage.reviewsCount", { count: order.clientInfo.reviews })}</span>
                              )}
                              <span className="hidden md:inline">•</span>
                              {order.clientInfo?.completedJobs && (
                                <span>{t("myOrdersPage.jobsCompleted", { count: order.clientInfo.completedJobs })}</span>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                              {order.clientInfo?.avgResponseTime && (
                                <div className="flex items-center gap-1">
                                  <Timer className="h-4 w-4" />
                                  <span>{t("myOrdersPage.responseTime", { time: order.clientInfo.avgResponseTime })}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-1">
                                <MapPin className="h-4 w-4" />
                                <span>{order.clientInfo?.location || order.clientInfo?.address || t("myOrdersPage.locationNotSpecified")}</span>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedClientUser(order.clientInfo);
                              setShowClientDialog(true);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-700 hover:underline self-start md:self-center"
                          >
                            {t("myOrdersPage.viewProfile")}
                          </button>
                        </div>
                      )}

                      <p className="text-sm text-gray-600 mt-2">
                        {order.description}
                      </p>
                    </div>
                    <div className="flex flex-row lg:flex-col lg:items-end justify-between lg:justify-start gap-2">
                      <div className="flex flex-col gap-2">
                        <StatusBadge status={order.status} />
                        {/* Refund Request Badge for Completed Contracts */}
                        {Number(order.status) === ContractStatusEnum.Completed && order.refundRequest && (
                          (() => {
                            const refundInfo = getRefundRequestInfo(order.refundRequest);
                            return (
                              <div className="flex flex-col gap-1">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${refundInfo?.className}`}>
                                  {refundInfo?.text}
                                </span>
                                <span className="text-xs text-red-600 font-medium">
                                  {getDynamicCurrencySymbol()}{order.refundRequest.amount?.toFixed(2) || "0.00"}
                                </span>
                              </div>
                            );
                          })()
                        )}
                      </div>
                      <div className="text-right">
                        {order.isHourly ? (
                          <div className="space-y-1">
                            <div className="text-sm text-gray-600">
                              {getDynamicCurrencySymbol()}{order.amount.toFixed(2)}/hour
                            </div>
                            <div className="text-lg font-medium text-gray-900">
                              {getDynamicCurrencySymbol()}{order.weeklyAmount.toFixed(2)}/week
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <div className="text-lg font-medium text-gray-900">
                              {getDynamicCurrencySymbol()}{order.amount.toFixed(2)}
                            </div>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        {t("myOrdersPage.due", { date: new Date(order.dueDate).toLocaleDateString() })}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
                    <div className="flex flex-wrap items-center gap-4">
                      <button className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
                        <MessageCircle className="h-4 w-4" />
                        <span className="hidden sm:inline">{t("myOrdersPage.messages", { count: order.messages })}</span>
                        <span className="sm:hidden">{order.messages}</span>
                      </button>
                      {order.attachments > 0 && (
                        <button className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
                          <FileText className="h-4 w-4" />
                          <span className="hidden sm:inline">{t("myOrdersPage.files", { count: order.attachments })}</span>
                          <span className="sm:hidden">{order.attachments}</span>
                        </button>
                      )}
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <StarRatingDisplay rating={order.clientRating} size="sm" />
                        <span>{order.clientRating ? order.clientRating.toFixed(1) : "0.0"}</span>
                      </div>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedOrder(order);
                      }}
                      className="flex items-center gap-2 px-3 py-1 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <span>{t("myOrdersPage.seeMore")}</span>
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Accept/Decline Contract Buttons */}
                  {canAcceptDeclineContract(order) && (
                    <div className="mt-4 pt-4 border-t">
                      {/* Smart Work Tracker Checkbox for Hourly Contracts */}
                      {requiresTrackerCheckbox(order) && (
                        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              id={`tracker-checkbox-list-${order.id}`}
                              checked={trackerCheckboxStates[order.id] || false}
                              onChange={(e) => {
                                e.stopPropagation();
                                setSelectedOrder(null);
                                handleTrackerCheckboxChange(order.id, e.target.checked);
                              }}
                              className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <label
                                  htmlFor={`tracker-checkbox-list-${order.id}`}
                                  className="text-sm font-medium text-gray-900 cursor-pointer"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {t("myOrdersPage.smartWorkTrackerAgreement")}
                                </label>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowServicesDialog(true);
                                  }}
                                  className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                                >
                                  {t("myOrdersPage.seeServices")}
                                </button>
                              </div>
                              <p className="text-xs text-gray-600 mt-1">
                                {t("myOrdersPage.trackerAgreementText")}
                              </p>

                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleContractAction(order.id, "accept");
                          }}
                          disabled={!!contractActionLoading[order.id] || (requiresTrackerCheckbox(order) && !trackerCheckboxStates[order.id])}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${(contractActionLoading[order.id] || (requiresTrackerCheckbox(order) && !trackerCheckboxStates[order.id]))
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-green-600 text-white hover:bg-green-700"
                            }`}
                        >
                          {contractActionLoading[order.id] === "accept" && <Loader className="h-4 w-4 animate-spin" />}
                          {contractActionLoading[order.id] === "accept" ? (
                            <span>{t("myOrdersPage.accepting")}</span>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4" />
                              <span>{t("myOrdersPage.acceptContract")}</span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleContractAction(order.id, "decline");
                          }}
                          disabled={!!contractActionLoading[order.id]}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${contractActionLoading[order.id]
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-red-600 text-white hover:bg-red-700"
                            }`}
                        >
                          {contractActionLoading[order.id] === "decline" && <Loader className="h-4 w-4 animate-spin" />}
                          {contractActionLoading[order.id] === "decline" ? (
                            <span>{t("myOrdersPage.declining")}</span>
                          ) : (
                            <>
                              <XCircle className="h-4 w-4" />
                              <span>{t("myOrdersPage.declineContract")}</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          <div className="mt-6">
            <nav className="flex items-center gap-2 justify-center">
              <button className="px-3 py-2 border rounded-lg disabled:opacity-50" disabled={pagination.currentPage === 1} onClick={() => handlePageChange(pagination.currentPage - 1)}>{t("myOrdersPage.previous")}</button>
              <span className="text-sm text-gray-600">{t("myOrdersPage.pageOf", { current: pagination.currentPage, total: Math.max(1, pagination.totalPages) })}</span>
              <button className="px-3 py-2 border rounded-lg disabled:opacity-50" disabled={!pagination.hasNextPage} onClick={() => handlePageChange(pagination.currentPage + 1)}>{t("myOrdersPage.next")}</button>
            </nav>
          </div>
        </Card>

        {/* Client Profile Dialog */}
        <AnimatePresence>
          {showClientDialog && selectedClientUser && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 z-50"
                onClick={() => setShowClientDialog(false)}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="fixed inset-4 z-50 overflow-hidden"
              >
                <div className="h-full bg-white rounded-lg shadow-xl flex flex-col">
                  <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {t("myOrdersPage.clientProfile", { 
                        name: (selectedClientUser?.firstName && selectedClientUser?.lastName)
                          ? `${selectedClientUser.firstName} ${selectedClientUser.lastName}`
                          : (selectedClientUser?.firstName || selectedClientUser?.name || "Client")
                      })}
                    </h2>
                    <button
                      onClick={() => setShowClientDialog(false)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4">
                    <ClientProfile1
                      isAdminEdit={true}
                      adminUserId={selectedClientUser.id}
                      canEdit={false}
                    />
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Order Details Modal */}
        <AnimatePresence>
          {selectedOrder && (
            <OrderDetailsModal
              order={selectedOrder}
              onClose={(messageData) => {
                setSelectedOrder(null);
                // If there's message data, we might want to handle it for real-time updates
                if (messageData) {
                  console.log("Modal closed with message data:", messageData);
                }
              }}
              onRefresh={(messageData) => {
                loadContracts(pagination.currentPage, pagination.limit);
                // Handle any real-time updates if message data is provided
                if (messageData) {
                  console.log("Refreshing with message data:", messageData);
                }
              }}
            />
          )}
        </AnimatePresence>

        {/* Smart Work Tracker Services Dialog */}
        <SmartWorkTrackerServicesDialog
          isOpen={showServicesDialog}
          onClose={() => setShowServicesDialog(false)}
        />
      </main>

      {/* Mobile Tab Menu */}
      <MobileTabMenu />

      {/* Add padding to prevent content from being hidden behind mobile menu */}
      <div className="h-20 lg:hidden" />

      {!loading && <SharedFooter3 />}

      {/* Profile Completion Dialog */}
      <ProfileCompletionDialog
        isOpen={showProfileCompletionDialog}
        onClose={() => setShowProfileCompletionDialog(false)}
        onGoToProfile={navigateToProfile}
        userRole={Number(user?.role?.id)}
      />

      {/* Verification Requirement Dialog */}
      <VerificationRequirementDialog
        isOpen={showVerificationDialog}
        onClose={() => setShowVerificationDialog(false)}
        title="Verification Required"
        message="Please complete your account verification to accept contracts."
      />

      {/* Billing Address Requirement Dialog */}
      <BillingAddressRequirementDialog
        isOpen={showBillingAddressDialog}
        onClose={() => {
          setShowBillingAddressDialog(false);
          setSelectedContractForBilling(null);
        }}
        onBillingSaved={handleBillingSaved}
        userRole={Number(user?.role?.id)}
        action="payment"
      />
    </div>
  );
};

export default OrdersDashboard;